import { useState, useMemo, useRef, useLayoutEffect, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { fromJS } from "immutable";
import { CircleAlert, Search, X } from "lucide-react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import InputDebounce from "./InputDebounce";
import useResizeObserver from "../useResizeObserver";
import { truncateText } from "../utils/TextUtil";
import { createItem, search } from "../api/ProjectApi";
import Avatar from "./Avatar";
import { teamOutboundRecipientsHistory } from "../api/EmailApi";
import { EventHistory } from "../activities/components/RecipientsSidebar";

const ChipRecipients = ({ id, projectId, recipients, showRemove, onRemove }) => {
  const { t } = useTranslation();
  const [containerLabelRef, size]: any = useResizeObserver();

  const { data } = useQuery({
    queryKey: [projectId, id, recipients],
    queryFn: async () => {
      return await search({
        projectId,
        pageSize: recipients.size,
        types: ["CONTACT", "COMPANY"],
        filters: {
          email: {
            operator: "in",
            values: recipients.toJS(),
          },
        },
      });
    },
    enabled: !!id && !!projectId && recipients.size > 0,
    staleTime: 60 * 1000, // cache 60s
    placeholderData: (previousData) => previousData,
  });

  const items = recipients.size > 0 ? fromJS(data?.pageResult?.content || []) : fromJS([]);
  const selectedRecipients = items.map((item) => ({
    id: item.getIn(["data", "email", "value"]),
    name: item.getIn(["data", "name", "value"]),
    displayName: truncateText(item.getIn(["data", "name", "value"])),
    iconUrl: item.get("iconUrl"),
  }));

  const allEmails = items
    .filter((item) => !!item.getIn(["data", "email", "value"]))
    .map((item) => item.getIn(["data", "email", "value"]))
    .toJS();

  const { data: teamOutboundRecipientsData } = useQuery({
    queryKey: ["team-outbound-recipients-history", projectId, allEmails],
    queryFn: async () => {
      return await teamOutboundRecipientsHistory(projectId, {
        emails: allEmails,
      });
    },
    enabled: !!projectId && allEmails.length > 0,
    staleTime: 60 * 1000, // cache 60s
  });

  const teamOutboundRecipients = fromJS(teamOutboundRecipientsData?.results || []);

  const visibleCount = useMemo(() => {
    if (selectedRecipients.size === 1) return 1;

    const gap = 4; // gap-1
    const estimateChipWidth = (text) => {
      let textWidth = text.length * 7; // approximate width per character
      if (textWidth < 14) {
        textWidth = 14; // minimum width for a chip
      }
      return textWidth + gap * 4 + (showRemove ? 16 : 0);
    };

    const width = size.width - 48;
    const viewAllWidth = estimateChipWidth(`+${selectedRecipients.size}`);
    let used = 0;
    let count = 0;

    const selectedRecipientData = selectedRecipients.toJS();

    for (let i = 0; i < selectedRecipientData.length; i++) {
      const chipWidth = estimateChipWidth(selectedRecipientData[i].displayName || "");
      const next = used + chipWidth + gap + viewAllWidth;
      if (next > width) break;
      used += chipWidth + gap;
      count++;
    }

    return count;
  }, [showRemove, selectedRecipients, size.width]);

  const titleSelectedRecipients = selectedRecipients
    .toJS()
    .slice(visibleCount, selectedRecipients.size)
    .map((item) => `${item.name} (${item.id})`)
    .join("\n");

  return (
    <div ref={containerLabelRef} className="w-full flex flex-nowrap gap-1 overflow-hidden">
      {selectedRecipients
        .toJS()
        .slice(0, visibleCount)
        .map((item) => {
          const { id, name, displayName, iconUrl } = item;
          const teamOutboundRecipient =
            teamOutboundRecipients.find((recipient) => recipient.get("email") === id) || fromJS({});
          const emailEvents = teamOutboundRecipient.get("events") || fromJS([]);
          const firstEvent = emailEvents.get(0) || fromJS({});
          const sentByDisplayName =
            id === firstEvent.get("sentByEmail") ? t("me") : firstEvent.get("sentByDisplayName");
          return (
            <div
              key={id}
              title={`${name} (${id})`}
              className={`shrink-0 flex items-center gap-2 rounded-md px-1 py-1 text-xs font-medium cursor-pointer hover:bg-gray-100`}
            >
              <Avatar avatarUrl={iconUrl} displayName={name} sizeIcon={6} />
              <div>
                <div>{displayName}</div>
                {sentByDisplayName ? (
                  <div className="flex items-center gap-1">
                    <CircleAlert className="w-3 h-3 text-red-500 shrink-0" />
                    <EventHistory events={emailEvents} />
                  </div>
                ) : null}
              </div>
              {showRemove ? (
                <X
                  className="w-3 h-3"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onRemove(id);
                  }}
                />
              ) : null}
            </div>
          );
        })}
      {visibleCount < selectedRecipients.size && (
        <button
          title={titleSelectedRecipients}
          className={`shrink-0 flex items-center gap-1 rounded-full px-2 py-1.5 text-xs font-medium leading-4 cursor-pointer bg-gray-100 text-gray-600`}
        >
          +{selectedRecipients.size - visibleCount}
        </button>
      )}
    </div>
  );
};

interface RecipientsSelectProps {
  id: string;
  projectId: string;
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  showRemove?: boolean;
  isDisabled?: boolean;
  testId?: string;
}

const RecipientsSelect = ({
  id,
  projectId,
  value,
  onChange,
  placeholder,
  showRemove = false,
  isDisabled = false,
  testId,
}: RecipientsSelectProps) => {
  const { t } = useTranslation();

  const popoverButtonRef = useRef(null);
  const popoverPanelRef = useRef(null);
  const [panelWidth, setPanelWidth] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const [keywords, setKeywords] = useState("");

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const { data, isLoading } = useQuery({
    queryKey: [projectId, keywords],
    queryFn: async () => {
      return await search({
        projectId: projectId,
        pageSize: 10,
        types: ["CONTACT", "COMPANY"],
        filters: {
          email: {
            operator: "regex",
            values: [keywords],
          },
        },
      });
    },
    enabled: isOpen && !!projectId,
    staleTime: 60 * 1000, // cache 60s
  });

  const items = fromJS(data?.pageResult?.content || []);

  const handleSelectItem = (item) => {
    if (!item.getIn(["data", "email", "value"])) return;
    if (value.includes(item.getIn(["data", "email", "value"]))) return;
    onChange([...value, item.getIn(["data", "email", "value"])]);
  };

  const renderSearch = () => {
    return (
      <div className="w-full flex items-center">
        <Search className="w-4 h-4 text-gray-400" />
        <InputDebounce
          autoFocus
          className={`w-full h-6 px-2 placeholder:text-gray-400 text-sm focus:outline-none`}
          placeholder={t("Search or enter an email to add new")}
          value={keywords}
          onChange={(value: string) => {
            setKeywords(value);
          }}
          delay={600}
        />
      </div>
    );
  };

  const handleAddNew = async (newEmail: string, onClose) => {
    const newItemData = {
      data: {
        name: { value: newEmail },
        email: { value: newEmail },
      },
      projectId: projectId,
      type: "CONTACT",
    };
    await createItem(newItemData);
    onChange([...value, newEmail]);
    onClose();
  };

  const renderRecipientList = (onClose) => {
    if (isLoading) {
      return (
        <div
          className="flex items-center justify-center p-1 text-gray-400"
          data-testid={`recipients-select-for-item-loading`}
        >
          {t("Loading...")}
        </div>
      );
    }
    const isAddNew = keywords && keywords.trim() && emailRegex.test(keywords);
    if (items.size === 0) {
      if (isAddNew) {
        return (
          <div
            className="p-1 text-sm rounded-md hover:bg-gray-100 transition-colors cursor-default"
            onClick={() => handleAddNew(keywords, onClose)}
          >
            {`${t("Add new")} "${keywords}"`}
          </div>
        );
      }
      return (
        <div
          className="flex items-center justify-center p-1 text-gray-400"
          data-testid={`recipients-select-for-item-no-options`}
        >
          {t("No options")}
        </div>
      );
    }
    const isEmailExisted =
      items.filter(
        (item) =>
          (item.getIn(["data", "email", "value"]) || "").toLowerCase() ===
          keywords.trim().toLowerCase()
      ).size > 0;
    return (
      <>
        {items.map((item, index) => {
          if (!item.getIn(["data", "email", "value"])) return null;
          return (
            <div
              key={item.get("id")}
              data-testid={`recipients-select-for-item-${index}`}
              className="flex items-center p-1 rounded-md hover:bg-gray-100 transition-colors gap-2 cursor-default"
              onClick={() => {
                handleSelectItem(item);
                onClose();
              }}
            >
              <div className="flex-none">
                <Avatar
                  avatarUrl={item.get("iconUrl")}
                  displayName={item.getIn(["data", "name", "value"])}
                />
              </div>
              <div className="flex flex-col flex-nowrap overflow-hidden">
                <div
                  title={item.getIn(["data", "name", "value"])}
                  className="text-sm font-medium leading-4 truncate"
                >
                  {item.getIn(["data", "name", "value"])}
                </div>
                <div
                  title={item.getIn(["data", "email", "value"])}
                  className="text-xs font-medium leading-4 text-gray-600 truncate"
                >
                  {item.getIn(["data", "email", "value"])}
                </div>
              </div>
            </div>
          );
        })}
        {!isEmailExisted && isAddNew && (
          <div
            className="p-1 text-sm rounded-md hover:bg-gray-100 transition-colors cursor-default"
            onClick={() => handleAddNew(keywords, onClose)}
          >
            {`${t("Add new")} "${keywords}"`}
          </div>
        )}
      </>
    );
  };

  const renderTarget = (isOpen) => {
    return (
      <div className={`w-full h-8 overflow-hidden gap-2 cursor-default`}>
        {value.length > 0 ? (
          <ChipRecipients
            id={id}
            projectId={projectId}
            recipients={fromJS(value)}
            showRemove={showRemove || isOpen}
            onRemove={(id) => {
              const datas = value.filter((val) => val !== id);
              onChange(datas);
            }}
          />
        ) : (
          <div className="h-full flex items-center flex-nowrap overflow-hidden">
            <div className="text-sm leading-4 text-gray-400 truncate">{placeholder}</div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Popover>
      {({ open, close }) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useLayoutEffect(() => {
          if (!open) return;
          if (!popoverButtonRef.current || !popoverPanelRef.current) return;
          const buttonWidth = popoverButtonRef.current.offsetWidth;
          const panelNaturalWidth = popoverPanelRef.current.offsetWidth;
          const maxWidth = Math.max(buttonWidth, panelNaturalWidth);
          setPanelWidth(maxWidth);
        }, [open]);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
          setIsOpen(open);
        }, [open]);

        return (
          <>
            <PopoverButton
              data-testid={testId}
              ref={popoverButtonRef}
              as="div"
              onClick={(e) => {
                if (isDisabled) {
                  e.preventDefault();
                }
                if (open) {
                  e.preventDefault();
                }
              }}
            >
              {renderTarget(open)}
            </PopoverButton>
            <PopoverPanel
              ref={popoverPanelRef}
              anchor="bottom start"
              style={{ width: panelWidth ?? "auto" }}
              className="z-9999 p-2 bg-white rounded-md border border-gray-300 shadow-xl mt-1"
            >
              <div>
                {renderSearch()}
                <div className="w-full my-1 border-b border-gray-200"></div>
                <div className="w-full max-h-60 overflow-y-auto">{renderRecipientList(close)}</div>
              </div>
            </PopoverPanel>
          </>
        );
      }}
    </Popover>
  );
};

export default RecipientsSelect;
